import React, { useCallback, useMemo, useState } from "react";
import { ContactDropdownProps, ContactDropdownTypeEnum } from "./types";

import { DropdownOption } from "../Dropdown/types";
import { Contact, ContactRow } from "services/api/contacts/types";
import { DropdownAutocomplete } from "../DropdownAutocomplete";
import ContactEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactEditCard/ContactEditCard";
import { Button } from "ui/components/Button/Button";
import { invalidateContacts, usePaginatedContacts, usePreselectedContact } from "./hooks";

/** Prikazni naziv kontakta — `name` (tvrtka ili ime+prezime), uz fallback ako name nije popunjen. */
const getContactName = (c: ContactRow | Contact): string =>
  (c.name ?? "").trim() ||
  `${c.companyName ?? ""} ${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();

const toContactOption = (c: ContactRow | Contact): DropdownOption => ({
  value: c.id,
  text: (
    <div className="flex items-center justify-between w-full">
      <span>{getContactName(c)}</span>
      <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">
        {c.contactType?.name ?? ""}
      </span>
    </div>
  ),
  image: c.pictures[0]?.url ?? "",
});

const ContactDropdown: React.FC<ContactDropdownProps> = (props) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    contactType,
    defaultContactType,
    contactGroupIds,
    tooltip,
  } = props;

  const toContactTypeId = (type?: ContactDropdownTypeEnum): string | null =>
    type === ContactDropdownTypeEnum.COMPANY
      ? "C"
      : type === ContactDropdownTypeEnum.PERSON
        ? "P"
        : null;

  // Filtar popisa; postoji samo kad je vrsta zaključana.
  const contactTypeId = toContactTypeId(contactType);

  // Vrsta s kojom se otvara unos novog kontakta. Zaključana vrsta ujedno sakriva korak
  // odabira tipa; predložena ga ostavlja, samo je unaprijed odabrana.
  const initialContactTypeId = contactTypeId ?? toContactTypeId(defaultContactType);

  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  // Tekst s kojim je unos pokrenut — modal njime popunjava naziv, da se ne tipka dvaput.
  const [newContactName, setNewContactName] = useState("");

  const { contacts, isLoading, hasMore, loadMore } = usePaginatedContacts(
    searchValue,
    contactTypeId,
    contactGroupIds
  );

  const preselectedContact = usePreselectedContact(value, contacts);

  const options: DropdownOption[] = useMemo(() => {
    const list = contacts.map(toContactOption);

    // Preselektirani kontakt s neke kasnije stranice — ubaci ga da se prikaže u inputu.
    if (preselectedContact && !contacts.some((c) => c.id === preselectedContact.id)) {
      list.unshift(toContactOption(preselectedContact));
    }

    return list;
  }, [contacts, preselectedContact]);

  const handleSearchFocus = useCallback(() => setSearchValue(""), []);
  const handleSearchChange = useCallback((val: string) => setSearchValue(val), []);

  // `DropdownAutocomplete` uz "Kreiraj …" šalje upisani tekst; gumb pored polja ga nema.
  const openAddModal = useCallback((e: any, name?: string) => {
    e.preventDefault();
    setNewContactName(name ?? "");
    setShowAddNewModal(true);
  }, []);

  const handleEditSuccess = useCallback(
    (contactId?: string) => {
      // Osvježi liste u SVIM dropdownima — novi kontakt mora biti vidljiv i u ostalima
      // (npr. kreiran na "Kontakt osiguranja", biran na "Kontakt kaska").
      invalidateContacts();
      setShowAddNewModal(false);
      // Novi kontakt je najnoviji pa uđe u osvježenu listu; ako ne, usePreselectedContact ga dohvati po id-u.
      if (contactId && onChange) {
        onChange({ target: { name, value: contactId } } as any, name, {
          value: contactId,
          text: "",
        });
      }
    },
    [name, onChange]
  );

  return (
    <>
      <div className="flex items-end ">
        <div className="flex-1">
          <DropdownAutocomplete
            label={label}
            name={name}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            value={value}
            direction={direction}
            width={width}
            height={height}
            options={options}
            onChange={onChange}
            onSearchChange={handleSearchChange}
            onSearchFocus={handleSearchFocus}
            onLoadMore={hasMore ? loadMore : undefined}
            error={error}
            onCreate={openAddModal}
            isLoading={isLoading}
            tooltip={tooltip}
            inputClassName={!disabled ? "!rounded-r-none" : undefined}
          />
        </div>
        {!disabled && (
          <Button
            variant="outlined"
            startIcon="/media/icons/plus.svg"
            onClick={openAddModal}
            disabled={disabled}
          />
        )}
      </div>

      <ContactEditCard
        contactId={null}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
        isFullScreen={false}
        initialContactTypeId={initialContactTypeId}
        lockContactType={!!contactTypeId}
        initialContactGroupIds={contactGroupIds}
        initialName={newContactName}
      />
    </>
  );
};

export default React.memo(ContactDropdown);
